import type { Track, TrackPoints } from '@/lib/db';
import { feetToMetres } from '@/lib/parse/units';

/**
 * A stored track written back out as a file.
 *
 * For the tracks with no original to hand back — anything fetched from a
 * network source, and anything imported before originals were kept. Both
 * formats are ones this app's own importer reads, deliberately: a file
 * downloaded from here and dropped back in, on this device or another, gives
 * the same flight, which is what makes the download a copy of the data
 * rather than a picture of it.
 *
 * GPX for everything else that reads tracks; the CSV in Flightradar24's own
 * column layout, because that is the layout every pilot who has exported from
 * FR24 already knows, and a spreadsheet opens it without a second thought.
 */

export type TrackExportFormat = 'gpx' | 'csv';

const KNOTS_TO_MPS = 1852 / 3600;

function finite(value: number | undefined): value is number {
  return value !== undefined && Number.isFinite(value);
}

function escapeXml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function identityOf(track: Track): string | undefined {
  return track.registration ?? track.callsign ?? track.icaoHex;
}

/**
 * GPX 1.0, not 1.1: 1.0 carries `<speed>` and `<course>` on a point as plain
 * children, where 1.1 moved them into vendor extensions that half the tools
 * reading GPX ignore. Groundspeed and track are most of what makes an ADS-B
 * track worth having, so the older schema is the one that keeps them.
 */
export function trackToGpx(track: Track, points: TrackPoints): string {
  const name = identityOf(track);
  const lines: string[] = [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<gpx version="1.0" creator="Flight Data Viewer" xmlns="http://www.topografix.com/GPX/1/0">',
    '  <trk>',
    ...(name === undefined ? [] : [`    <name>${escapeXml(name)}</name>`]),
    '    <trkseg>',
  ];

  for (let i = 0; i < points.lat.length; i += 1) {
    const lat = points.lat[i];
    const lon = points.lon[i];
    if (!finite(lat) || !finite(lon)) {
      continue;
    }
    const children: string[] = [];
    const altFt = points.altFt[i];
    if (finite(altFt)) {
      children.push(`<ele>${feetToMetres(altFt).toFixed(1)}</ele>`);
    }
    const t = points.t[i];
    if (finite(t)) {
      children.push(`<time>${new Date(t).toISOString()}</time>`);
    }
    const trackDeg = points.trackDeg[i];
    if (finite(trackDeg)) {
      children.push(`<course>${trackDeg.toFixed(0)}</course>`);
    }
    const gsKt = points.gsKt[i];
    if (finite(gsKt)) {
      children.push(`<speed>${(gsKt * KNOTS_TO_MPS).toFixed(2)}</speed>`);
    }
    lines.push(
      `      <trkpt lat="${lat.toFixed(6)}" lon="${lon.toFixed(6)}">${children.join('')}</trkpt>`,
    );
  }

  lines.push('    </trkseg>', '  </trk>', '</gpx>', '');
  return lines.join('\n');
}

/** `Timestamp,UTC,Callsign,Position,Altitude,Speed,Direction` — FR24's header, verbatim. */
export function trackToCsv(track: Track, points: TrackPoints): string {
  // FR24 writes the registration without its hyphen, and `registrationFromCallsign`
  // puts it back on the way in.
  const callsign = (track.callsign ?? track.registration ?? '').replace('-', '');
  const cell = (value: number | undefined, digits: number): string =>
    finite(value) ? value.toFixed(digits) : '';

  const lines = ['Timestamp,UTC,Callsign,Position,Altitude,Speed,Direction'];
  for (let i = 0; i < points.lat.length; i += 1) {
    const lat = points.lat[i];
    const lon = points.lon[i];
    if (!finite(lat) || !finite(lon)) {
      continue;
    }
    const t = points.t[i];
    lines.push(
      [
        finite(t) ? String(Math.round(t / 1000)) : '',
        finite(t) ? new Date(t).toISOString().replace(/\.\d{3}Z$/, 'Z') : '',
        callsign,
        `"${lat.toFixed(6)},${lon.toFixed(6)}"`,
        cell(points.altFt[i], 0),
        cell(points.gsKt[i], 0),
        cell(points.trackDeg[i], 0),
      ].join(','),
    );
  }
  lines.push('');
  return lines.join('\n');
}

/**
 * `ZK-ABC_2026-09-15_2128Z.gpx` — sortable by name, and in UTC so the name
 * of a file never depends on the zone of the device that wrote it.
 */
export function exportFilename(track: Track, format: TrackExportFormat): string {
  const identity = (identityOf(track) ?? 'track').replace(/[^A-Za-z0-9-]/g, '');
  const start = new Date(track.startTime);
  const stamp = Number.isNaN(start.getTime())
    ? 'undated'
    : `${start.toISOString().slice(0, 10)}_${start.toISOString().slice(11, 16).replace(':', '')}Z`;
  return `${identity}_${stamp}.${format}`;
}

export function exportTrack(
  track: Track,
  points: TrackPoints,
  format: TrackExportFormat,
): { filename: string; mediaType: string; text: string } {
  return format === 'gpx'
    ? {
        filename: exportFilename(track, 'gpx'),
        mediaType: 'application/gpx+xml',
        text: trackToGpx(track, points),
      }
    : {
        filename: exportFilename(track, 'csv'),
        mediaType: 'text/csv',
        text: trackToCsv(track, points),
      };
}

/**
 * Hand the browser a file to save.
 *
 * The object URL is revoked a while later rather than straight after
 * `click()`: Safari starts the download asynchronously, and a URL revoked
 * before it has read the blob cancels a multi-megabyte download silently.
 * A minute is FileSaver.js's long-standing margin, rounded up.
 */
export function saveFile(filename: string, data: BlobPart, mediaType: string): void {
  const url = URL.createObjectURL(new Blob([data], { type: mediaType }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 60_000);
}

/**
 * On a phone, the share sheet; everywhere else, a download.
 *
 * iOS has no Downloads folder a pilot would look in: a downloaded `.fdv`
 * lands in a preview with nothing to preview. The share sheet's Save to
 * Files puts it where it was meant to go — iCloud Drive, or the sync folder
 * itself. A desktop browser also has a share sheet, but a download is what
 * people expect there.
 *
 * Sharing needs the click that started it to still count as a user gesture.
 * Building a large archive can outlast that, in which case the browser
 * refuses the share and this falls back to a download rather than failing.
 *
 * @returns `'cancelled'` when the pilot closed the share sheet.
 */
export async function shareOrSaveFile(
  filename: string,
  data: BlobPart,
  mediaType: string,
): Promise<'shared' | 'saved' | 'cancelled'> {
  const nav = globalThis.navigator as Navigator | undefined;
  const mobile = /iPhone|iPad|iPod|Android/.test(nav?.userAgent ?? '');
  if (mobile && typeof nav?.canShare === 'function') {
    const file = new File([data], filename, { type: mediaType });
    if (nav.canShare({ files: [file] })) {
      try {
        await nav.share({ files: [file] });
        return 'shared';
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          return 'cancelled';
        }
      }
    }
  }
  saveFile(filename, data, mediaType);
  return 'saved';
}
